import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useAuth from '../../../Hooks/useAuth';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import { ACTION_POPUP_CONTENT_WIDTH_CLASS } from '../../../components/popup/popupLayoutSpec';
import { useInlineRowActions, ROW_ACTIONS_MENU_HEIGHT } from '../../../Hooks/useInlineRowActions';
import { useConfigurationListPagination } from '../../../Hooks/useConfigurationListPagination';
import { urlNameSegment } from '../../../utils/slugify';
import {
  AdminPageTitle,
  AdminPanelTitle,
  AdminHeaderAction,
  AdminTableHeader,
  AdminTableBody,
  AdminTableMeta,
  AdminRowMenuItem,
  AdminRowActionsButton,
  AdminFixedRowActionsMenu,
  AdminRowMenuIcon,
  AdminSupportingText,
  ADMIN_PAGE_TITLE_CLASS,
  ADMIN_TABLE_CLASS,
} from '../shared/adminUx';
import {
  CONTENT_CONTROL_ROUTE,
  buildSampleContentControlRules,
  formatConditionsPreview,
  formatDetectionMethodList,
  formatTriggerList,
  getContentAreaLabel,
  getContentContextLabel,
  getContentIssueLabel,
  getModuleLabel,
  normalizeContentControlForm,
  sortContentControlRules,
} from '../../../data/contentControlConfig';
import {
  canAddContentControlRule,
  canEditContentControlRule,
  canViewContentControl,
} from '../../../data/contentControlRuleWorkflow';

const tableCols =
  'minmax(0, 0.45fr) minmax(0, 1.15fr) minmax(0, 0.65fr) minmax(0, 0.85fr) minmax(0, 0.8fr) minmax(0, 0.85fr) minmax(0, 0.95fr) minmax(0, 0.85fr) minmax(0, 0.95fr) minmax(62px, 0.04fr)';

const ViewAllModal = ({ title, items, onClose }) => (
  <div
    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4"
    onClick={onClose}
    role="presentation"
  >
    <div
      className={`bg-white rounded-xl shadow-lg ${ACTION_POPUP_CONTENT_WIDTH_CLASS} max-h-[90vh] overflow-y-auto`}
      onClick={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="cct-view-all-title"
    >
      <div className="relative px-5 pt-5 pb-3 border-b border-lightgray">
        <div id="cct-view-all-title">
          <AdminPanelTitle className="text-offblack text-center">{title}</AdminPanelTitle>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 text-offblack hover:text-primaryblue"
          aria-label="Close"
        >
          <i className="fi fi-rs-cross text-sm" aria-hidden />
        </button>
      </div>
      <div className="px-5 py-4">
        <ul className="divide-y divide-lightgray border-y border-lightgray">
          {items.map((item, index) => (
            <li key={`${item}-${index}`} className="py-2.5 break-words">
              <AdminTableBody>{item}</AdminTableBody>
            </li>
          ))}
        </ul>
        <div className="flex justify-end mt-4">
          <AdminHeaderAction onClick={onClose}>Close</AdminHeaderAction>
        </div>
      </div>
    </div>
  </div>
);

const ContentControl = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { auth } = useAuth();
  const canView = canViewContentControl(auth);
  const canAdd = canAddContentControlRule(auth);
  const canEdit = canEditContentControlRule(auth);

  const [rules, setRules] = useState(() => sortContentControlRules(buildSampleContentControlRules()));
  const [viewAllConditions, setViewAllConditions] = useState(null);

  const {
    rowActionsOpen,
    rowActionsRefs,
    tableScrollRef,
    rowActionsMenuLayout,
    rowActionsMenuRef,
    rowActionsMenuPlacementReady,
    toggleRowActions,
    closeRowActions,
  } = useInlineRowActions(ROW_ACTIONS_MENU_HEIGHT.DOUBLE);

  useEffect(() => {
    const incoming = location.state?.rules;
    if (Array.isArray(incoming)) {
      setRules(sortContentControlRules(incoming.map(normalizeContentControlForm)));
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const displayedRules = useMemo(() => sortContentControlRules(rules), [rules]);

  const {
    currentPage,
    setCurrentPage,
    totalPages,
    paginatedItems: paginatedRules,
    rangeStart,
    rangeEnd,
    totalCount,
    showPagination,
  } = useConfigurationListPagination(displayedRules);

  const openRuleView = (rule) => {
    navigate(`${CONTENT_CONTROL_ROUTE}/${urlNameSegment(rule.ruleName)}`, {
      state: { rule, existingRules: rules },
    });
  };
  const openRuleEdit = (rule) => {
    navigate(`${CONTENT_CONTROL_ROUTE}/${urlNameSegment(rule.ruleName)}/edit`, {
      state: { rule, existingRules: rules },
    });
  };

  const columnHeaders = [
    'Rule ID',
    'Rule Name',
    'Module',
    'Content Area',
    'Content Context',
    'Triggers',
    'Content Issue',
    'Conditions',
    'Detection Methods',
    'Actions',
  ];

  if (!canView) {
    return (
      <AdminLayout>
        <AdminPageTitle>Content Control</AdminPageTitle>
        <AdminSupportingText className="text-mediumgray mt-4">
          You do not have permission to view this Rule.
        </AdminSupportingText>
      </AdminLayout>
    );
  }

  const emptyMessage = 'No Content Control Rules found.';

  return (
    <AdminLayout>
      <div className="min-w-0">
        <div className={`flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 ${ADMIN_PAGE_TITLE_CLASS}`}>
          <AdminPageTitle className="!mb-0">Content Control</AdminPageTitle>
          {canAdd ? (
            <AdminHeaderAction onClick={() => navigate(`${CONTENT_CONTROL_ROUTE}/add`, { state: { existingRules: rules } })}>
              Add Rule
            </AdminHeaderAction>
          ) : null}
        </div>

        <DashboardMainContainer className={`${ADMIN_TABLE_CLASS} max-w-full min-w-0`}>
          <div ref={tableScrollRef} className="overflow-x-auto">
            <div
              className="grid w-full py-3 text-offblack border-b border-lightgray px-4 gap-x-3 gap-y-2"
              style={{ gridTemplateColumns: tableCols }}
            >
              {columnHeaders.map((label) => (
                <div
                  key={label}
                  className={`flex items-center min-w-0 ${label === 'Actions' ? 'justify-center shrink-0' : ''}`}
                >
                  <AdminTableHeader className={label === 'Actions' ? '' : 'truncate'}>{label}</AdminTableHeader>
                </div>
              ))}
            </div>

            {displayedRules.length === 0 ? (
              <div className="py-10 px-4 text-center space-y-4">
                <AdminSupportingText className="text-offblack font-medium">{emptyMessage}</AdminSupportingText>
                {!rules.length && canAdd ? (
                  <div className="flex justify-center pt-2">
                    <AdminHeaderAction onClick={() => navigate(`${CONTENT_CONTROL_ROUTE}/add`, { state: { existingRules: rules } })}>
                      Add Rule
                    </AdminHeaderAction>
                  </div>
                ) : null}
              </div>
            ) : (
              paginatedRules.map((rule) => {
                const normalized = normalizeContentControlForm(rule);
                const conditionsPreview = formatConditionsPreview(normalized.conditions);
                return (
                  <div
                    key={normalized.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => openRuleView(normalized)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openRuleView(normalized);
                      }
                    }}
                    className="grid w-full py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-x-3 gap-y-2 cursor-pointer rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-primaryblue focus-visible:ring-offset-1"
                    style={{ gridTemplateColumns: tableCols }}
                  >
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <AdminTableMeta className="truncate block" title={normalized.id}>{normalized.id}</AdminTableMeta>
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <AdminTableBody className="truncate block" title={normalized.ruleName}>{normalized.ruleName}</AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <AdminTableBody className="truncate block">{getModuleLabel(normalized.module)}</AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <AdminTableBody className="truncate block">{getContentAreaLabel(normalized.contentArea)}</AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <AdminTableBody className="truncate block">{getContentContextLabel(normalized.contentContext)}</AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <AdminTableBody className="truncate block" title={formatTriggerList(normalized.triggers)}>
                        {formatTriggerList(normalized.triggers)}
                      </AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <AdminTableBody className="truncate block">{getContentIssueLabel(normalized.contentIssue)}</AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      {conditionsPreview.extraCount > 0 ? (
                        <AdminTableBody className="block min-w-0 w-full" title={conditionsPreview.lines.join(', ')}>
                          <span className="flex items-center min-w-0 w-full gap-1">
                            <span className="min-w-0 truncate">{conditionsPreview.lines[0]}</span>
                            <button
                              type="button"
                              className="text-primaryblue hover:underline shrink-0 whitespace-nowrap"
                              onClick={(e) => {
                                e.stopPropagation();
                                setViewAllConditions(conditionsPreview.lines);
                              }}
                            >
                              +{conditionsPreview.extraCount} more
                            </button>
                          </span>
                        </AdminTableBody>
                      ) : (
                        <AdminTableBody className="truncate block" title={conditionsPreview.text}>
                          {conditionsPreview.text}
                        </AdminTableBody>
                      )}
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <AdminTableBody className="truncate block" title={formatDetectionMethodList(normalized.detectionMethods)}>
                        {formatDetectionMethodList(normalized.detectionMethods)}
                      </AdminTableBody>
                    </div>
                    <div className="flex items-center justify-center shrink-0 overflow-visible" onClick={(e) => e.stopPropagation()}>
                      <div
                        className="relative inline-flex"
                        ref={(el) => {
                          rowActionsRefs.current[normalized.id] = el;
                        }}
                      >
                        <AdminRowActionsButton
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRowActions(normalized.id);
                          }}
                          aria-expanded={!!rowActionsOpen[normalized.id]}
                          title="Actions"
                          aria-label="Row actions"
                        >
                          <AdminRowMenuIcon />
                        </AdminRowActionsButton>
                        {rowActionsOpen[normalized.id] && rowActionsMenuLayout && (
                          <AdminFixedRowActionsMenu layout={rowActionsMenuLayout} menuRef={rowActionsMenuRef} placementReady={rowActionsMenuPlacementReady}>
                            <AdminRowMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                closeRowActions();
                                openRuleView(normalized);
                              }}
                            >
                              View
                            </AdminRowMenuItem>
                            {canEdit ? (
                              <AdminRowMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  closeRowActions();
                                  openRuleEdit(normalized);
                                }}
                              >
                                Edit
                              </AdminRowMenuItem>
                            ) : null}
                          </AdminFixedRowActionsMenu>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <ConfigurationListPaginationFooter
            show={showPagination}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            totalCount={totalCount}
            currentPage={currentPage}
            totalPages={totalPages}
            onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
            goToPage={setCurrentPage}
          />
        </DashboardMainContainer>
      </div>

      {viewAllConditions ? (
        <ViewAllModal title="Conditions" items={viewAllConditions} onClose={() => setViewAllConditions(null)} />
      ) : null}
    </AdminLayout>
  );
};

export default ContentControl;
